<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import { getObservationList, getSpecieDetail, getObservationBySiteGeo } from "@carnet/api/backoffice";
import type { ObservationSpecieFilters } from "@/components/WebMapPopupObservations.vue";
import { apiBool, throwOnNotFound } from "@/utils/api";

definePageMeta({
  name: "species.detail",
  layout: "app",
});

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: "/especes/[id]",
  },
});

const { t } = useI18n();
const localePath = useLocalePath();
const isMobile = useIsMobile();
const route = useRoute("species.detail");
const { mapbox: mapboxAllowed } = useCookieConsent();

const {
  data: specie,
  pending,
  error,
  refresh,
} = await getSpecieDetail({
  composable: "useFetch",
  key: `specie-${route.params.id}`,
  path: {
    locale: appLocale(),
    id: route.params.id as string,
  },
});

throwOnNotFound(error.value, "Espèce introuvable");

const detail = computed(() => specie.value?.data);

const pageTitle = computed(() => detail.value?.commonName || t("species.title"));

useSeoMeta({
  title: () => formatPageTitle(pageTitle.value, t("species.title")),
  description: () =>
    metaDescription(detail.value?.description) ??
    t("seo.specie.description", { name: detail.value?.commonName ?? t("species.title") }),
});
useSeoImage(() => seoImageFrom(detail.value?.mainImage, pageTitle.value));

useJsonLd(() => {
  const specie = detail.value;
  if (!specie) return null;
  return {
    "@type": "Taxon",
    name: specie.commonName,
    alternateName: specie.scientificName || undefined,
    parentTaxon: specie.taxonList.length ? specie.taxonList : undefined,
    description: metaDescription(specie.description),
    image: specie.mainImage?.url,
  };
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: t("species.title"), to: localePath({ name: "species" }) },
  { label: pageTitle.value },
]);

const specieIdNum = computed(() => Number(route.params.id));

const observationFilters = computed<ObservationSpecieFilters>(() => ({ specieId: specieIdNum.value }));

const { data: geoData } = await getObservationBySiteGeo({
  composable: "useLazyFetch",
  key: `specie-geo-${route.params.id}`,
  path: { locale: appLocale() },
  query: computed(() => ({ specieId: specieIdNum.value })),
});

const geoMarkers = computed(() => geoData.value?.data?.geojson ?? { type: "FeatureCollection" as const, features: [] });

// Carrousel sous la ligne de flottaison : ne bloque ni le SSR ni la navigation.
const { data: recentPhotosData } = await getObservationList({
  composable: "useLazyFetch",
  key: `specie-${route.params.id}-recent-photos`,
  path: { locale: appLocale() },
  query: { specieId: specieIdNum.value, withPhotoOnly: apiBool(true), size: 3, page: 1, sort: "date_desc" },
});

const recentPhotos = computed(() => recentPhotosData.value?.data?.list ?? []);
</script>

<template>
  <div class="flex flex-col">
    <AppSection class="bg-elevated" :ui="{ base: 'pt-8' }">
      <WebBreadcrumbs :items="breadcrumb" />
    </AppSection>

    <AppContentSpinner v-if="pending" class="bg-elevated grow" />

    <AppSection v-else-if="error" class="bg-elevated grow" :ui="{ base: 'py-8' }">
      <WebErrorRetry @retry="refresh()" />
    </AppSection>

    <template v-else-if="detail">
      <AppSpecieDetails :detail="detail">
        <template v-if="geoMarkers.features.length" #observations-map>
          <AppSpecieObservationsMap :geo="geoMarkers" :mapbox-allowed="mapboxAllowed" :is-mobile="isMobile">
            <template #disabled>
              <WebCookieConsentMapPlaceholder class="size-full rounded-(--ui-radius)" />
            </template>
            <template #popup="{ siteId, close }">
              <WebMapPopupObservations v-if="siteId" :site-id="siteId" :filters="observationFilters" @close="close" />
            </template>
          </AppSpecieObservationsMap>
        </template>
      </AppSpecieDetails>

      <AppSection v-if="recentPhotos.length" class="bg-white overflow-hidden" :ui="{ base: 'py-20' }">
        <AppCommunityObservations
          :title="t('species.detail.recent_photos')"
          :items="recentPhotos"
          :item-key="(observation) => observation.id"
          :see-all-to="$localePath({ name: 'observations.specie', params: { specieId: route.params.id } })"
          :see-all-label="t('species.detail.see_all_photos')"
        >
          <template #default="{ item, index }">
            <AppTileObservationPhoto
              :observation="item"
              :to="
                $localePath({
                  name: 'observations.specie.photo',
                  params: { specieId: route.params.id, photoIndex: index + 1 },
                  query: { from: route.fullPath },
                })
              "
              :alt="detail.commonName"
            />
          </template>
        </AppCommunityObservations>
      </AppSection>
    </template>

    <p v-else class="bg-elevated grow px-10 py-8 text-blue-600">
      {{ t("species.none") }}
    </p>
  </div>
</template>
